import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { FileText, Clock, MapPin, Star, Shield, CreditCard, Lock } from 'lucide-react';
import { Button } from './Button';
export function Hero() {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <section className="relative pt-8 sm:pt-20 pb-8 sm:pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden" data-id="element-152">
      {/* Background Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-96 bg-blue-600/20 blur-[120px] rounded-full pointer-events-none" data-id="element-153" />

      <motion.div initial="hidden" animate="visible" variants={stagger} className="relative z-10 text-center max-w-4xl mx-auto" data-id="element-154">
        <motion.div variants={fadeIn} className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-slate-800/80 border border-slate-700 mb-3 sm:mb-8" data-id="element-155">
          <span className="font-bold text-blue-400 text-sm sm:text-base" data-id="element-156">
            PACKVIAJERO™
          </span>
          <span className="text-lg sm:text-xl" data-id="element-157">🇬🇧</span>
        </motion.div>

        <motion.h1 variants={fadeIn} className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight mb-3 sm:mb-6 leading-tight" data-id="element-158">
          Todo lo que necesitás para tu viaje a Inglaterra. <br data-id="element-159" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600" data-id="element-160">
            Listo. Desde hoy.
          </span>
        </motion.h1>

        {/* Product Image */}
        <motion.div variants={fadeIn} className="flex justify-center mb-3 sm:mb-8" data-id="element-161">
          <motion.img src="https://cdn.magicpatterns.com/uploads/6ReqRxrVwnrzohCU8tu2SS/ChatGPT_Image_23_abr_2026__02_04_30_a.m.-removebg-preview.png" alt="PackViajero Megapack - 4 Tomos + 4 Bonos + Guía Expert" className="w-full max-w-[200px] sm:max-w-xs md:max-w-sm" animate={{
          y: [0, -6, 0]
        }} transition={{
          repeat: Infinity,
          duration: 4,
          ease: 'easeInOut'
        }} data-id="element-162" />
        </motion.div>

        <motion.p variants={fadeIn} className="text-base sm:text-xl md:text-2xl text-slate-300 mb-3 sm:mb-8 font-medium" data-id="element-163">
          El sistema N°1 para el viajero argentino que viaja al exterior sin
          saber inglés
        </motion.p>

        <motion.div variants={fadeIn} className="hidden sm:flex flex-wrap justify-center gap-3 mb-10 text-sm md:text-base font-medium text-slate-300" data-id="element-164">
          <span className="flex items-center gap-1.5 bg-slate-800/50 px-3 py-1.5 rounded-lg border border-slate-700/50" data-id="element-165">
            <FileText className="w-4 h-4 text-blue-400" data-id="element-166" /> 100% PDF
          </span>
          <span className="flex items-center gap-1.5 bg-slate-800/50 px-3 py-1.5 rounded-lg border border-slate-700/50" data-id="element-167">
            <Clock className="w-4 h-4 text-amber-400" data-id="element-168" /> Acceso en 5 minutos
          </span>
          <span className="flex items-center gap-1.5 bg-slate-800/50 px-3 py-1.5 rounded-lg border border-slate-700/50" data-id="element-169">
            🇦🇷 Hecho para argentinos
          </span>
          <span className="flex items-center gap-1.5 bg-slate-800/50 px-3 py-1.5 rounded-lg border border-slate-700/50" data-id="element-170">
            🗣️ De cero al inglés que necesitás
          </span>
        </motion.div>

        <motion.p variants={fadeIn} className="hidden sm:block text-lg text-slate-400 mb-8" data-id="element-171">
          4 Tomos + 4 Bonos + Guía Expert — Principiante → Avanzado — Todas las
          situaciones
        </motion.p>

        <motion.div variants={fadeIn} className="flex flex-col items-center gap-3 sm:gap-4" data-id="element-172">
          <Button href="#pricing" className="text-base sm:text-xl px-6 sm:px-10 py-3.5 sm:py-5 w-full sm:w-auto" data-id="element-173">
            → QUIERO MI PACKVIAJERO AHORA
          </Button>

          <div className="flex flex-wrap justify-center gap-3 md:gap-6 text-xs sm:text-sm text-slate-400 mt-1 sm:mt-4" data-id="element-174">
            <span className="flex items-center gap-1.5" data-id="element-175">
              <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-400" data-id="element-176" />{' '}
              Garantía 7 días
            </span>
            <span className="flex items-center gap-1.5" data-id="element-177">
              <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-300" data-id="element-178" /> Pago
              seguro
            </span>
            <span className="flex items-center gap-1.5" data-id="element-179">
              <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-400" data-id="element-180" />{' '}
              Mercado Pago
            </span>
          </div>
        </motion.div>

        {/* Stats Bar */}
        <motion.div variants={fadeIn} className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 p-6 rounded-2xl glass-panel" data-id="element-181">
          <div className="text-center" data-id="element-182">
            <div className="text-3xl font-bold text-white mb-1" data-id="element-183">+1.200</div>
            <div className="text-sm text-slate-400" data-id="element-184">viajeros que lo usaron</div>
          </div>
          <div className="text-center" data-id="element-185">
            <div className="text-3xl font-bold text-white mb-1" data-id="element-186">4</div>
            <div className="text-sm text-slate-400" data-id="element-187">Tomos completos</div>
          </div>
          <div className="text-center" data-id="element-188">
            <div className="text-3xl font-bold text-white mb-1" data-id="element-189">4</div>
            <div className="text-sm text-slate-400" data-id="element-190">Bonos incluidos</div>
          </div>
          <div className="text-center" data-id="element-191">
            <div className="text-3xl font-bold text-amber-400 mb-1 flex items-center justify-center gap-1" data-id="element-192">
              4.9/5 <Star className="w-5 h-5 fill-amber-400 text-amber-400" data-id="element-193" />
            </div>
            <div className="text-sm text-slate-400" data-id="element-194">calificación</div>
          </div>
        </motion.div>

        {/* Hero Testimonials */}
        <motion.div variants={fadeIn} className="mt-16 text-left" data-id="element-195">
          <p className="text-center text-slate-400 mb-8 font-medium" data-id="element-196">
            Lo que dicen los que ya viajaron con PackViajero:
          </p>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-197">
            {[{
            text: 'Llegué a Heathrow sin saber nada y con el Tomo 1 ya pude hablar con el de migraciones. No lo puedo creer.',
            author: 'Fernanda G., Córdoba'
          }, {
            text: 'El Tomo 4 me salvó en Oxford Street. Reconocí una estafa en segundos y la corté como decía la guía.',
            author: 'Lucas P., Rosario'
          }, {
            text: 'Lo leí en el avión. Al bajar ya sabía tomar el Tube, pedir en el pub y decir que no con firmeza. Vale cada peso.',
            author: 'Romina M., Buenos Aires'
          }].map((test, i) => <div key={i} className="glass-panel p-6 rounded-xl relative" data-id="element-198">
                <div className="flex gap-1 mb-3" data-id="element-199">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" data-id="element-200" />)}
                </div>
                <p className="text-slate-300 text-sm italic mb-4" data-id="element-201">
                  "{test.text}"
                </p>
                <p className="text-slate-400 text-xs font-semibold" data-id="element-202">
                  — {test.author}
                </p>
              </div>)}
          </div>
        </motion.div>
      </motion.div>
    </section>;
}